import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowDown, Search, Loader2, MapPin, TrendingDown, Route, Timer, RefreshCw, Share2, Check, LogIn, LogOut, Star, StarOff, Shield } from "lucide-react";
import { LocationInput } from "../components/LocationInput";
import { FareCard } from "../components/FareCard";
import { RIDE_TYPES } from "../lib/providers";
import { compareFares, getComparison } from "../lib/api";
import {
  fetchMe,
  logout as apiLogout,
  startGoogleLogin,
  listFavorites,
  addFavorite,
  deleteFavorite,
} from "../lib/auth";

export default function Home() {
  const { compId } = useParams();
  const navigate = useNavigate();
  const [pickup, setPickup] = useState(null);
  const [drop, setDrop] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [filter, setFilter] = useState("All");
  const [copied, setCopied] = useState(false);

  // Auth + user data
  const [user, setUser] = useState(null);
  const [authChecking, setAuthChecking] = useState(true);
  const [favorites, setFavorites] = useState([]);
  const [savingFav, setSavingFav] = useState(false);

  // Check auth on mount (skip if returning from OAuth callback)
  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setAuthChecking(false);
      return;
    }
    (async () => {
      try {
        const me = await fetchMe();
        setUser(me);
      } catch (e) {
        setUser(null);
      } finally {
        setAuthChecking(false);
      }
    })();
  }, []);

  // Load favorites when signed in
  const reloadUserData = useCallback(async () => {
    if (!user) return;
    try {
      const f = await listFavorites();
      setFavorites(f);
    } catch (e) {
      // ignore — favorites are optional
    }
  }, [user]);

  useEffect(() => { reloadUserData(); }, [reloadUserData, result]);

  // Load shared comparison from URL
  useEffect(() => {
    if (!compId) return;
    (async () => {
      setLoading(true);
      try {
        const data = await getComparison(compId);
        setResult(data);
        if (data.pickup) setPickup(data.pickup);
        if (data.drop) setDrop(data.drop);
        toast.success("Loaded shared comparison");
      } catch (err) {
        toast.error("This shared link is invalid or expired");
        navigate("/", { replace: true });
      } finally {
        setLoading(false);
      }
    })();
  }, [compId, navigate]);

  const onCompare = async () => {
    if (!pickup || !drop) {
      toast.error("Please select both pickup and drop-off locations");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const data = await compareFares(
        { lat: pickup.lat, lon: pickup.lon, label: pickup.label },
        { lat: drop.lat, lon: drop.lon, label: drop.label }
      );
      setResult(data);
      toast.success(`Found ${data.offers.length} rides across 6 providers`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const filteredOffers = useMemo(() => {
    if (!result) return [];
    if (filter === "All") return result.offers;
    return result.offers.filter((o) => o.ride_type === filter);
  }, [result, filter]);

  const cheapestKey = result?.cheapest_id;
  const savings = useMemo(() => {
    if (!result?.offers?.length) return 0;
    const min = Math.min(...result.offers.map((o) => o.fare));
    const max = Math.max(...result.offers.map((o) => o.fare));
    return max - min;
  }, [result]);

  const pickFromRecent = (it) => {
    setPickup(it.pickup);
    setDrop(it.drop);
    setResult(null);
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 50);
  };

  const isFavorite = useMemo(() => {
    if (!pickup || !drop || !favorites?.length) return null;
    return favorites.find(
      (f) =>
        Math.abs(f.pickup.lat - pickup.lat) < 0.0005 &&
        Math.abs(f.pickup.lon - pickup.lon) < 0.0005 &&
        Math.abs(f.drop.lat - drop.lat) < 0.0005 &&
        Math.abs(f.drop.lon - drop.lon) < 0.0005
    );
  }, [pickup, drop, favorites]);

  const toggleFavorite = async () => {
    if (!user) {
      toast.error("Sign in to save favorites");
      return;
    }
    if (!pickup || !drop) return;
    setSavingFav(true);
    try {
      if (isFavorite) {
        await deleteFavorite(isFavorite.id);
        toast.success("Removed from favorites");
      } else {
        const label = `${(pickup.label || "").split(",")[0]} → ${(drop.label || "").split(",")[0]}`;
        await addFavorite({ label, pickup, drop });
        toast.success("Saved to favorites");
      }
      await reloadUserData();
    } catch (e) {
      toast.error("Couldn't update favorite");
    } finally {
      setSavingFav(false);
    }
  };

  const handleLogout = async () => {
    try {
      await apiLogout();
    } catch (e) {
      // ignore — cookie will be gone client-side on reload anyway
    }
    setUser(null);
    setFavorites([]);
    toast.success("Signed out");
  };

  const reset = () => {
    setResult(null);
    setPickup(null);
    setDrop(null);
    setFilter("All");
    setCopied(false);
    if (compId) navigate("/", { replace: true });
  };

  const shareLink = async () => {
    if (!result?.id) return;
    const url = `${window.location.origin}/r/${result.id}`;
    try {
      if (navigator.share) {
        await navigator.share({
          title: "Cab Compare — Cab price comparison",
          text: `Cheapest cab ride: ${result.offers[0].provider} ${result.offers[0].ride_type} at ₹${result.offers[0].fare}`,
          url,
        });
        return;
      }
    } catch (e) {
      // user cancelled or share failed — fall through to clipboard copy
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      toast.error("Couldn't copy link");
    }
  };

  return (
    <div className="min-h-screen bg-white text-zinc-950" style={{ fontFamily: "'Outfit', sans-serif" }}>
      {/* Header */}
      <header className="border-b border-zinc-100 bg-white/80 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-md mx-auto lg:max-w-4xl px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2" data-testid="app-logo">
            <div className="w-9 h-9 rounded-xl bg-zinc-950 flex items-center justify-center">
              <Route className="text-white" size={18} />
            </div>
            <div>
              <div className="font-black text-lg tracking-tight leading-none">Cab Compare</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">
                Cheapest cab in seconds
              </div>
            </div>
          </div>
          {result && (
            <button
              onClick={reset}
              data-testid="new-search-btn"
              className="text-xs font-bold uppercase tracking-wider text-zinc-600 hover:text-zinc-950 inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-zinc-200 hover:border-zinc-950 transition-all"
            >
              <RefreshCw size={12} /> New
            </button>
          )}
          {!result && !authChecking && (
            user ? (
              <div className="flex items-center gap-2">
                <div
                  data-testid="user-chip"
                  className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-zinc-200 bg-white"
                >
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-zinc-950 text-white text-[10px] font-bold flex items-center justify-center">
                      {(user.name || user.email || "U")[0].toUpperCase()}
                    </div>
                  )}
                  <span className="text-xs font-semibold text-zinc-900 max-w-[80px] truncate">
                    {(user.name || user.email).split(" ")[0]}
                  </span>
                </div>
                <button
                  onClick={handleLogout}
                  data-testid="logout-btn"
                  aria-label="Sign out"
                  className="p-2 rounded-full border border-zinc-200 hover:border-zinc-950 transition-all"
                >
                  <LogOut size={12} className="text-zinc-600" />
                </button>
              </div>
            ) : (
              <button
                onClick={startGoogleLogin}
                data-testid="signin-btn"
                className="text-xs font-bold uppercase tracking-wider text-white bg-zinc-950 hover:bg-zinc-800 inline-flex items-center gap-1.5 px-3 py-2 rounded-full transition-all"
              >
                <LogIn size={12} /> Sign in
              </button>
            )
          )}
        </div>
      </header>

      <main className="max-w-md mx-auto lg:max-w-4xl px-4 sm:px-6 py-8 pb-24">
        {/* Hero */}
        {!result && (
          <section className="mb-6" data-testid="hero-section">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-zinc-950 leading-[0.95]">
              One search.
              <br />
              <span className="inline-flex items-center gap-2">
                Six apps.
                <span className="inline-block w-3 h-3 rounded-full bg-green-500 animate-pulse" />
              </span>
              <br />
              <span className="text-green-600">Lowest fare wins.</span>
            </h1>
            <p className="mt-4 text-base text-zinc-600 leading-relaxed max-w-md">
              Compare Uber, Ola, Rapido, BluSmart, inDrive & Meru side-by-side. No login. No fluff. Just the best price.
            </p>
          </section>
        )}

        {/* Input group */}
        <section
          className="flex flex-col gap-4 p-5 sm:p-6 bg-white rounded-3xl border border-zinc-200 shadow-sm relative"
          data-testid="search-panel"
        >
          <LocationInput
            label="Pickup"
            placeholder="Where are you?"
            value={pickup}
            onSelect={setPickup}
            testId="pickup-input"
            iconColor="text-green-600"
            showLocateMe={true}
          />

          <div className="flex justify-center -my-1">
            <div className="w-8 h-8 rounded-full bg-zinc-50 border border-zinc-200 flex items-center justify-center">
              <ArrowDown size={14} className="text-zinc-500" />
            </div>
          </div>

          <LocationInput
            label="Drop-off"
            placeholder="Where to?"
            value={drop}
            onSelect={setDrop}
            testId="drop-input"
            iconColor="text-red-600"
          />

          <button
            onClick={onCompare}
            disabled={loading || !pickup || !drop}
            data-testid="compare-button"
            className="w-full mt-2 py-4 rounded-2xl bg-zinc-950 text-white font-semibold text-lg hover:bg-zinc-800 transition-all transform active:scale-[0.98] shadow-md disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="animate-spin" size={18} /> Comparing…
              </>
            ) : (
              <>
                <Search size={18} /> Compare fares
              </>
            )}
          </button>
        </section>

        {/* Results */}
        {result && (
          <section className="mt-8" data-testid="results-section">
            {/* Share bar */}
            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 inline-flex items-center gap-2">
                {result.offers.length} rides found
                <span
                  data-testid="route-source-badge"
                  className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest ${
                    result.route_source === "osrm"
                      ? "bg-green-100 text-green-800"
                      : "bg-zinc-100 text-zinc-700"
                  }`}
                >
                  {result.route_source === "osrm" ? "Live route" : "Estimated"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {user && (
                  <button
                    onClick={toggleFavorite}
                    disabled={savingFav}
                    data-testid="favorite-toggle"
                    className={
                      isFavorite
                        ? "text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5 px-3 py-2 rounded-full border transition-all bg-amber-50 border-amber-300 text-amber-700"
                        : "text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5 px-3 py-2 rounded-full border transition-all border-zinc-200 text-zinc-950 hover:border-amber-400"
                    }
                  >
                    {isFavorite ? <Star size={12} fill="currentColor" /> : <StarOff size={12} />}
                    {isFavorite ? "Saved" : "Save"}
                  </button>
                )}
                <button
                  onClick={shareLink}
                  data-testid="share-button"
                  className="text-xs font-bold uppercase tracking-wider text-zinc-950 hover:text-green-700 inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-zinc-200 hover:border-green-600 transition-all"
                >
                  {copied ? <Check size={12} /> : <Share2 size={12} />}
                  {copied ? "Copied" : "Share"}
                </button>
              </div>
            </div>

            {/* Summary */}
            <div className="grid grid-cols-3 gap-3 mb-5">
              <StatBox
                icon={<Route size={14} />}
                label="Distance"
                value={`${result.distance_km} km`}
                testId="stat-distance"
              />
              <StatBox
                icon={<Timer size={14} />}
                label="Est. time"
                value={`${result.duration_minutes} min`}
                testId="stat-duration"
              />
              <StatBox
                icon={<TrendingDown size={14} />}
                label="You save"
                value={`₹${savings}`}
                highlight
                testId="stat-savings"
              />
            </div>

            {/* Filter chips */}
            <div
              className="flex gap-2 overflow-x-auto no-scrollbar pb-2 -mx-4 px-4 sm:mx-0 sm:px-0"
              data-testid="filter-chips"
            >
              {RIDE_TYPES.map((t) => {
                const active = filter === t;
                return (
                  <button
                    key={t}
                    onClick={() => setFilter(t)}
                    data-testid={`filter-${t.toLowerCase()}`}
                    className={
                      active
                        ? "px-4 py-2 rounded-full bg-zinc-950 text-white text-sm font-semibold transition-all whitespace-nowrap shrink-0"
                        : "px-4 py-2 rounded-full border border-zinc-200 bg-white text-zinc-600 text-sm font-medium hover:bg-zinc-50 hover:border-zinc-400 transition-all whitespace-nowrap shrink-0"
                    }
                  >
                    {t}
                  </button>
                );
              })}
            </div>

            {/* Fare list */}
            <div className="mt-5 flex flex-col gap-3" data-testid="fare-list">
              {filteredOffers.length === 0 && (
                <div className="text-center py-10 text-zinc-500 text-sm">
                  No offers for this ride type.
                </div>
              )}
              {filteredOffers.map((offer, idx) => {
                const id = `${offer.provider}-${offer.ride_type}`;
                return (
                  <FareCard
                    key={id}
                    offer={offer}
                    isCheapest={filter === "All" && id === cheapestKey}
                    index={idx}
                  />
                );
              })}
            </div>

            {/* Route info */}
            <div className="mt-6 p-4 rounded-2xl bg-zinc-50 border border-zinc-200 text-xs text-zinc-600 leading-relaxed">
              <div className="flex items-start gap-2">
                <MapPin size={12} className="mt-0.5 text-green-600 shrink-0" />
                <span className="truncate">{pickup?.label}</span>
              </div>
              <div className="flex items-start gap-2 mt-1.5">
                <MapPin size={12} className="mt-0.5 text-red-600 shrink-0" />
                <span className="truncate">{drop?.label}</span>
              </div>
            </div>
          </section>
        )}

        {!result && !loading && (
          <>
            {user && favorites?.length > 0 && (
              <section className="mt-8" data-testid="favorites-section">
                <div className="flex items-center gap-2 mb-3">
                  <Star size={12} className="text-amber-500" fill="currentColor" />
                  <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
                    Your favorites
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  {favorites.slice(0, 5).map((f, idx) => (
                    <div
                      key={f.id}
                      className="group px-4 py-3 rounded-2xl border border-amber-200 bg-amber-50 flex items-center justify-between gap-3"
                      data-testid={`favorite-${idx}`}
                    >
                      <button
                        onClick={() => pickFromRecent(f)}
                        className="text-left flex-1 min-w-0"
                      >
                        <div className="text-sm font-semibold text-zinc-950 truncate">{f.label}</div>
                        <div className="text-[11px] text-zinc-500 mt-0.5 truncate">
                          {(f.pickup?.label || "").split(",")[0]} → {(f.drop?.label || "").split(",")[0]}
                        </div>
                      </button>
                      <button
                        onClick={async () => {
                          await deleteFavorite(f.id);
                          await reloadUserData();
                          toast.success("Removed");
                        }}
                        aria-label="Remove favorite"
                        data-testid={`favorite-${idx}-remove`}
                        className="p-1.5 rounded-full hover:bg-amber-100 transition-colors"
                      >
                        <StarOff size={14} className="text-amber-700" />
                      </button>
                    </div>
                  ))}
                </div>
              </section>
            )}

            <section className="mt-10">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-3">
                How it works
              </div>
              <ol className="space-y-3 text-sm text-zinc-700">
                <li className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-zinc-950 text-white text-xs font-bold flex items-center justify-center shrink-0">1</span>
                  <span>Enter your pickup and drop-off — we auto-suggest from OpenStreetMap.</span>
                </li>
                <li className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-zinc-950 text-white text-xs font-bold flex items-center justify-center shrink-0">2</span>
                  <span>We estimate fares across 6 apps and 5 ride types instantly.</span>
                </li>
                <li className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-zinc-950 text-white text-xs font-bold flex items-center justify-center shrink-0">3</span>
                  <span>Tap <strong>Book</strong> to open your chosen app and confirm the ride.</span>
                </li>
              </ol>
              <div className="mt-4 text-[11px] text-zinc-500 inline-flex items-center gap-1.5">
                <Shield size={11} /> Protected by rate limiting — 30 searches/min per IP.
              </div>
            </section>
          </>
        )}
      </main>

      <footer className="border-t border-zinc-100 mt-10">
        <div className="max-w-md mx-auto lg:max-w-4xl px-4 sm:px-6 py-6 text-xs text-zinc-500">
          Fares are estimates based on distance & time — actual prices may vary due to surge, tolls, and app promos. Cab Compare is not affiliated with any listed provider.
        </div>
      </footer>
    </div>
  );
}

const StatBox = ({ icon, label, value, highlight, testId }) => (
  <div
    data-testid={testId}
    className={`p-3 rounded-xl border ${
      highlight ? "bg-green-50 border-green-200" : "bg-white border-zinc-200"
    }`}
  >
    <div className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest ${highlight ? "text-green-700" : "text-zinc-500"}`}>
      {icon} {label}
    </div>
    <div className={`mt-1 font-black text-lg tabular-nums tracking-tight ${highlight ? "text-green-700" : "text-zinc-950"}`}>
      {value}
    </div>
  </div>
);
